import './Products.css';
import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { useProducts } from './useProducts';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import SlInput from '@shoelace-style/shoelace/dist/react/input/index.js';
import SlTooltip from '@shoelace-style/shoelace/dist/react/tooltip/index.js';
import SlAlert from '@shoelace-style/shoelace/dist/react/alert/index.js';
import SlSpinner from '@shoelace-style/shoelace/dist/react/spinner/index.js';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AppContext, appContext } from '../../../context/AppContext';
import {
	formatCurrency,
	LocaleCode,
	useDeferredLoading,
	ProductOrderBy,
	Product,
	ProductAvailability,
	IntegrationMetadata,
} from '@anticatrade/shared';
import { Link, Spinner, EmptyState, Pagination, Select } from '@anticatrade/shared';
import anticatradeLogo from '../../../assets/logo-icon-publish.svg';
import { Heading } from '../../base/Heading/Heading';
import { AddProductDialog } from './AddProductDialog';
import { PublishDialog } from './PublishDialog';
import { useMerchantIntegrations } from './useMerchantIntegrations';
import { ExtensionPublishResult, useExtension } from './useExtension';
import * as Sentry from '@sentry/react';

const Products = () => {
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
	const [productToPublishID, setProductToPublishID] = useState<number | null>(null);

	const { t, i18n } = useTranslation();

	const { merchant, showStatus } = useContext(appContext as React.Context<AppContext>);

	const {
		isLoading,
		products,
		count,
		page,
		limit,
		setLimit,
		drafts,
		deleteDraft,
		deletingDraftIds,
		searchInput,
		setSearchInput,
		orderBy,
		onOrderByChange,
		updateProductPublications,
	} = useProducts();

	const { isLoadingIntegrations, merchantIntegrations, integrationByKey } =
		useMerchantIntegrations();

	const { isExtensionAvailable, requestPublish } = useExtension(
		(result: ExtensionPublishResult) => {
			// Update the dialog list of publications to show the
			// destination as flagged.
			if (result.ok) {
				const product = products?.find((p) => p.id === result.job.productID);
				const existing = product?.publications ?? [];
				if (
					!existing.some(
						(p) => p.merchantIntegrationID === result.job.merchantIntegrationID,
					)
				) {
					updateProductPublications(result.job.productID, [
						...existing,
						{
							merchantIntegrationID: result.job.merchantIntegrationID,
							integrationKey: result.job.integrationKey,
							externalID: result.externalID ?? null,
						},
					]);
				}
				showStatus({
					variant: 'success',
					icon: 'check2-circle',
					text: t('Item published on the marketplace'),
				});
			} else {
				const message = result.error ?? 'unknown error';
				console.error('Extension publish failed:', message, result.job);
				Sentry.captureException(new Error(`Extension publish failed: ${message}`), {
					tags: { integrationKey: result.job.integrationKey },
					extra: {
						productID: result.job.productID,
						merchantIntegrationID: result.job.merchantIntegrationID,
					},
				});
				showStatus({
					variant: 'danger',
					icon: 'exclamation-triangle',
					text:
						result.errorCode === 'DailyPublicationLimitReached'
							? t('Daily publish limit reached')
							: t('Could not publish the item on the marketplace'),
				});
			}
		},
	);

	const location = useLocation();

	const isInitialLoading = isLoading && products === undefined;
	const isRefreshing = isLoading && products !== undefined;

	const { showSpinner, showContent } = useDeferredLoading(isInitialLoading);

	useEffect(() => {
		if (!location.hash || products == null || showSpinner) {
			return;
		}
		const el = document.querySelector(location.hash);
		if (el != null) {
			el.scrollIntoView({ block: 'center' });
		}
	}, [products, location.hash, showSpinner]);

	const onChangeLimit = (e: any) => {
		setLimit(Number(e.target.value));
	};

	const onSearchChange = (e: any) => {
		setSearchInput(e.target.value);
	};

	const onSortChange = (e: any) => {
		onOrderByChange(e.target.value as ProductOrderBy);
	};

	let headingCount: ReactNode = null;
	if (!isLoading) {
		const start = (page - 1) * limit + 1;
		let end = start + limit - 1;
		if (end > count!) {
			end = count!;
		}
		headingCount = `(${start}-${end} ${t('Of')} ${count})`;
	}

	const hasProducts = products?.length != null;
	const showProducts = products?.length != null && products!.length > 0;
	const hasDrafts = drafts?.length > 0;
	let hasExpiredSubscription = false;
	let hasReachedSubscriptionLimit = false;
	if (merchant != null) {
		hasExpiredSubscription = new Date(merchant.subscriptionEnd!) < new Date();
		hasReachedSubscriptionLimit = merchant.productUsed! >= merchant.subscriptionLimit!;
	}

	const hasIntegrations = merchant?.features?.includes('integrations') ?? false;

	const productToPublish = products?.find((p) => p.id === productToPublishID) ?? null;

	return (
		<div className='products'>
			{showContent ? (
				<div className='products__wrapper'>
					<Heading
						left={
							<div className='products__heading-title'>
								<h1 className='products__heading-title-text'>{t('Catalog')}</h1>
								{showProducts && (
									<span className='products__heading-count'>{headingCount}</span>
								)}
							</div>
						}
						right={
							(hasProducts || hasDrafts) && (
								<SlButton
									className='products__add-button products__add-button--mobile'
									variant='primary'
									disabled={hasExpiredSubscription || hasReachedSubscriptionLimit}
									onClick={() => setIsAddDialogOpen(true)}
								>
									{t('Add')}
								</SlButton>
							)
						}
					/>
					{(hasProducts || hasDrafts) && (
						<div className='products__toolbar'>
							{hasProducts && (
								<div className='products__search-row'>
									<SlInput
										className='products__search-input'
										type='text'
										placeholder={t('Search items...')}
										value={searchInput}
										onSlInput={onSearchChange}
									/>
									<Select
										className='products__sort-select'
										value={orderBy}
										onChange={onSortChange}
										options={[
											{
												key: 'datedesc',
												name: t('Most recent'),
												value: 'datedesc',
											},
											{
												key: 'dateasc',
												name: t('Least recent'),
												value: 'dateasc',
											},
											{
												key: 'popularity',
												name: t('Most popular'),
												value: 'popularity',
											},
											{
												key: 'pricedesc',
												name: t('Price: high to low'),
												value: 'pricedesc',
											},
											{
												key: 'priceasc',
												name: t('Price: low to high'),
												value: 'priceasc',
											},
										]}
									/>
								</div>
							)}
							<SlButton
								className='products__add-button products__add-button--desktop'
								variant='primary'
								disabled={hasExpiredSubscription || hasReachedSubscriptionLimit}
								onClick={() => setIsAddDialogOpen(true)}
							>
								{t('Add')}
							</SlButton>
						</div>
					)}
					{hasDrafts && (
						<div className='products__drafts'>
							<div className='products__drafts-title'>{t('Drafts')}</div>
							<div className='products__drafts-list'>
								{drafts.map((d) => (
									<div className='products__draft' key={d.id}>
										<div className='products__draft-info'>
											<Link path={`add?draft=${d.id}`}>
												<div className='products__draft-image'>
													{d.imageKeys.length > 0 ? (
														<img
															src={`${import.meta.env.VITE_IMAGES_DISTRIBUTION}/${d.imageKeys[0]}`}
														/>
													) : (
														<SlIcon
															name='image'
															className='products__draft-icon'
														/>
													)}
												</div>
											</Link>
											<div className='products__draft-text'>
												<Link path={`add?draft=${d.id}`}>
													<span className='products__draft-title'>
														{d.formData.title || t('Untitled draft')}
													</span>
												</Link>
												<span className='products__draft-date'>
													{new Date(d.updatedAt).toLocaleString(
														i18n.language,
													)}
												</span>
											</div>
										</div>
										<div className='products__draft-actions'>
											<Link path={`add?draft=${d.id}`}>
												<SlButton
													variant='default'
													size='small'
													disabled={deletingDraftIds.has(d.id)}
												>
													{t('Continue')}
												</SlButton>
											</Link>
											<SlButton
												variant='default'
												size='small'
												onClick={() => deleteDraft(d.id)}
												disabled={deletingDraftIds.has(d.id)}
												loading={deletingDraftIds.has(d.id)}
											>
												{t('Delete')}
											</SlButton>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
					{hasProducts && showProducts && (
						<div className={isRefreshing ? 'products__content--refreshing' : undefined}>
							<div className='products__products'>
								{hasExpiredSubscription && (
									<SlAlert
										className='products__products-alert'
										variant='danger'
										open
									>
										<SlIcon slot='icon' name='exclamation-square-fill' />
										{t('Subscription expired message')}
									</SlAlert>
								)}
								{hasReachedSubscriptionLimit && (
									<SlAlert
										className='products__products-alert'
										variant='danger'
										open
									>
										<SlIcon slot='icon' name='exclamation-square-fill' />
										{t('Subscription limit reached message')}
									</SlAlert>
								)}
								<div
									className={`products__table${
										hasIntegrations ? ' products__table--integrations' : ''
									}`}
								>
									<div className='products__row products__row--head'>
										<span className='products__cell products__cell--image' />
										<span className='products__cell products__cell--title'>
											{t('Item')}
										</span>
										<span className='products__cell products__cell--price'>
											{t('Price')}
										</span>
										<span className='products__cell products__cell--status'>
											{t('Availability')}
										</span>
										{hasIntegrations && (
											<span className='products__cell products__cell--published'>
												{t('Published on')}
											</span>
										)}
										<span className='products__cell products__cell--actions' />
									</div>
									{products!.map((p) => {
										const texts = p.texts[i18n.language as LocaleCode];
										const isCompleted = p.status === 'completed';
										const isProcessing = p.status === 'processing';
										const isFailed = p.status === 'failed';
										const showImageSpinner =
											!isFailed && (isProcessing || p.imageReprocessing);
										const imgSrc = isCompleted
											? `${import.meta.env.VITE_PRODUCT_IMAGE_URL}/${p.id}/thumbnail-${p.image.fileName}.webp`
											: `${import.meta.env.VITE_PRODUCT_IMAGE_URL.replace(/\/products$/, '/products-upload')}/${p.id}/main-${p.image.fileName}.${p.image.originalExtension}`;
										const path = `/products/${encodeURIComponent(p.id)}?returnPage=${page}`;
										return (
											<div
												className='products__row'
												id={`product-${p.id}`}
												key={p.id}
											>
												<Link
													path={path}
													className='products__cell products__cell--image'
												>
													<div className='products__image'>
														{showImageSpinner ? (
															<SlTooltip
																content={t(
																	'Images are being processed',
																)}
																hoist
															>
																<SlSpinner
																	style={
																		{
																			fontSize: '26px',
																			'--track-width': '4px',
																		} as React.CSSProperties
																	}
																/>
															</SlTooltip>
														) : (
															<img src={imgSrc} />
														)}
													</div>
												</Link>
												<div className='products__cell products__cell--title'>
													<Link
														path={path}
														className='products__title-link'
													>
														<span className='products__title'>
															{texts.title}
															{p.sku != null &&
																` (${
																	p.sku.startsWith('#')
																		? p.sku
																		: '#' + p.sku
																})`}
														</span>
													</Link>
													{!hasIntegrations &&
														!isProcessing &&
														!isFailed && (
															<SlTooltip
																content={
																	p.visible
																		? t(
																				`Visible in Anticatrade's showcase`,
																			)
																		: t(
																				`Not visible in Anticatrade's showcase`,
																			)
																}
																hoist
															>
																<SlIcon
																	className={`products__visibility products__visibility--${
																		p.visible
																			? 'visible'
																			: 'hidden'
																	}`}
																	name={
																		p.visible
																			? 'check-circle-fill'
																			: 'x-circle-fill'
																	}
																/>
															</SlTooltip>
														)}
												</div>
												<div className='products__cell products__cell--price'>
													{formatCurrency(p.price, i18n.language, 'EUR')}
												</div>
												<div className='products__cell products__cell--status'>
													<ProductStatusBadge product={p} />
												</div>
												{hasIntegrations && (
													<div className='products__cell products__cell--published'>
														<PublishedOn
															product={p}
															integrationByKey={integrationByKey}
														/>
													</div>
												)}
												<div className='products__cell products__cell--actions'>
													<Link path={path} aria-label={t('Edit')}>
														<SlButton
															className='products__edit-button'
															variant='default'
															aria-label={t('Edit')}
														>
															<SlIcon slot='prefix' name='pencil' label={t('Edit')} />
														</SlButton>
													</Link>
													{isCompleted && hasIntegrations && (
														<SlButton
															className='products__publish-button'
															variant='default'
															aria-label={t('Publish on')}
															onClick={() =>
																setProductToPublishID(p.id)
															}
														>
															<SlIcon
																slot='prefix'
																name='three-dots-vertical'
																label={t('Publish on')}
															/>
														</SlButton>
													)}
												</div>
											</div>
										);
									})}
								</div>
							</div>
							<div className='products__pagination'>
								{count! > 5 && (
									<div className='products__limit'>
										<span className='products__limit-label'>
											{t('Per page')}
										</span>
										<Select
											value={String(limit)}
											onChange={onChangeLimit}
											options={[
												{
													key: '5',
													name: '5',
													value: '5',
												},
												{
													key: '10',
													name: '10',
													value: '10',
												},
												{
													key: '20',
													name: '20',
													value: '20',
												},
												{
													key: '50',
													name: '50',
													value: '50',
												},
												{
													key: '100',
													name: '100',
													value: '100',
												},
											]}
										/>
									</div>
								)}
								<Pagination
									count={count!}
									limit={limit}
									page={page}
									setPage={null}
									t={t}
								/>
							</div>
						</div>
					)}
					{!hasProducts && !hasDrafts && (
						<div
							className={`wrapped${isRefreshing ? ' products__content--refreshing' : ''}`}
						>
							<EmptyState
								title={t('You have not added any item yet')}
								description={t(
									'The items you add will appear on this page. You can then access each item to edit or delete it',
								)}
								icon='boxes'
							>
								<SlButton
									className='products__add-button'
									variant='primary'
									onClick={() => setIsAddDialogOpen(true)}
								>
									{t('Add the first item')}
								</SlButton>
							</EmptyState>
						</div>
					)}
					{hasProducts && !showProducts && (
						<div
							className={`wrapped${isRefreshing ? ' products__content--refreshing' : ''}`}
						>
							<p className='products__no-results'>
								{t('No results found for this search')}
							</p>
						</div>
					)}
				</div>
			) : (
				showSpinner && <Spinner />
			)}
			<AddProductDialog
				isOpen={isAddDialogOpen}
				onClose={() => setIsAddDialogOpen(false)}
				disabled={hasExpiredSubscription || hasReachedSubscriptionLimit}
			/>
			<PublishDialog
				product={productToPublish}
				merchantIntegrations={merchantIntegrations}
				isLoading={isLoadingIntegrations}
				isExtensionAvailable={isExtensionAvailable}
				onClose={() => setProductToPublishID(null)}
				onPublicationsChange={updateProductPublications}
				requestPublish={requestPublish}
			/>
		</div>
	);
};

const ProductStatusBadge = ({ product }: { product: Product }) => {
	const { t } = useTranslation();

	if (product.status === 'failed') {
		return (
			<SlTooltip content={t('Image processing failed')} hoist>
				<span className='products__status products__status--failed'>{t('Failed')}</span>
			</SlTooltip>
		);
	}
	if (product.status === 'processing' || product.imageReprocessing) {
		return (
			<span className='products__status products__status--processing'>{t('Processing')}</span>
		);
	}

	const labels: Record<ProductAvailability, string> = {
		available: t('Available'),
		reserved: t('Reserved'),
		sold: t('Sold'),
	};
	return (
		<span className={`products__status products__status--${product.availability}`}>
			{labels[product.availability]}
		</span>
	);
};

const PublishedOn = ({
	product,
	integrationByKey,
}: {
	product: Product;
	integrationByKey: Map<string, IntegrationMetadata>;
}) => {
	const { t } = useTranslation();

	return (
		<div className='products__badges'>
			{product.visible && product.status === 'completed' && (
				<SlTooltip
					content={t('Published on {{destination}}', { destination: 'Anticatrade' })}
					hoist
				>
					<div className='products__badge'>
						<img src={anticatradeLogo} alt='Anticatrade' />
					</div>
				</SlTooltip>
			)}

			{(product.publications ?? [])
				.flatMap((pub) => {
					const meta = integrationByKey.get(pub.integrationKey);
					return meta == null ? [] : [{ pub, meta }];
				})
				.sort((a, b) => a.meta.title.localeCompare(b.meta.title))
				.map(({ pub, meta }) => (
					<SlTooltip
						key={pub.merchantIntegrationID}
						content={t('Published on {{destination}}', { destination: meta.title })}
						hoist
					>
						<div className='products__badge'>
							<img src={meta.logoURL} alt={meta.title} />
						</div>
					</SlTooltip>
				))}
		</div>
	);
};

export { Products };
